import { ASK_HELIX_STRATEGIC_EXECUTION_CAPACITY_EVIDENCE_SCHEMA_VERSION } from '../ask-helix';
import { defineModuleEvidenceContract, defineModuleEvidenceEntry } from './moduleEvidenceContract';
import { DECISION_AGENT_SOURCE_CLASSES } from './sourceClasses';

export const STRATEGIC_EXECUTION_CAPACITY_EVIDENCE_KEYS = {
  governedSnapshot: 'strategic_execution_capacity_evidence',
  tenantPortfolioCompanyProfile: 'tenant_portfolio_company_profile',
  gapSnapshot: 'strategic_execution_gap_snapshot',
  capacityBaseline: 'capacity_baseline',
  initiativePortfolioPlan: 'initiative_portfolio_plan',
  executionActuals: 'execution_actuals',
  executionProgressActual: 'execution_progress_actual',
  constraintAndDependencyEvidence: 'constraint_and_dependency_evidence',
  allocationPolicy: 'allocation_policy',
  initiativeExecutionPlan: 'initiative_execution_plan',
  resourceCapacityActual: 'resource_capacity_actual',
  executionControlPolicy: 'execution_control_policy',
  initiativeCapacityBinding: 'initiative_capacity_binding',
  dependencyBlockerRegister: 'dependency_blocker_register',
  scenarioActionPlan: 'scenario_action_plan',
  decisionLedgerCommitments: 'decision_ledger_commitments',
  capabilityQualificationFact: 'capability_qualification_fact',
  performanceMeasurementBaseline: 'performance_measurement_baseline',
  initiativeCostActual: 'initiative_cost_actual',
  costForecastPolicy: 'cost_forecast_policy',
  integratedMasterSchedule: 'integrated_master_schedule',
  scheduleRiskInput: 'schedule_risk_input',
  workItemFlowActual: 'work_item_flow_actual',
  benefitRealizationPlan: 'benefit_realization_plan',
  benefitRealizationActual: 'benefit_realization_actual',
  operatingOutcomeFact: 'operating_outcome_fact',
  changeImpactAssessment: 'change_impact_assessment',
  decisionLedgerVerificationPlan: 'decision_ledger_verification_plan',
  approvedFormulaRuntimeReceipt: 'approved_formula_runtime_receipt',
} as const;

export const STRATEGIC_EXECUTION_CAPACITY_STATE_KEYS = {
  governedSnapshot: STRATEGIC_EXECUTION_CAPACITY_EVIDENCE_KEYS.governedSnapshot,
  tenantPortfolioCompanyProfile:
    STRATEGIC_EXECUTION_CAPACITY_EVIDENCE_KEYS.tenantPortfolioCompanyProfile,
  gapSnapshot: STRATEGIC_EXECUTION_CAPACITY_EVIDENCE_KEYS.gapSnapshot,
  capacityBaseline: STRATEGIC_EXECUTION_CAPACITY_EVIDENCE_KEYS.capacityBaseline,
  initiativePortfolioPlan: STRATEGIC_EXECUTION_CAPACITY_EVIDENCE_KEYS.initiativePortfolioPlan,
  executionActuals: STRATEGIC_EXECUTION_CAPACITY_EVIDENCE_KEYS.executionActuals,
  constraintAndDependencyEvidence:
    STRATEGIC_EXECUTION_CAPACITY_EVIDENCE_KEYS.constraintAndDependencyEvidence,
  allocationPolicy: STRATEGIC_EXECUTION_CAPACITY_EVIDENCE_KEYS.allocationPolicy,
} as const;

export const STRATEGIC_EXECUTION_CAPACITY_CONTEXT_KINDS = {
  governedSnapshot: 'portfolio_planner',
} as const;

export const STRATEGIC_EXECUTION_CAPACITY_SCHEMA_VERSIONS = {
  manifest: 'decision_agent_module_manifest_v1',
  governedSnapshot: ASK_HELIX_STRATEGIC_EXECUTION_CAPACITY_EVIDENCE_SCHEMA_VERSION,
  contextPack: 'decision_agent_context_pack_v1',
  decisionLedgerVerificationPlan: 'decision_ledger_verification_plan_v1',
} as const;

export const STRATEGIC_EXECUTION_CAPACITY_ADMITTED_SOURCE_CLASSES = {
  csvAdminUpload: DECISION_AGENT_SOURCE_CLASSES.csvAdminUpload,
  connectorDerived: DECISION_AGENT_SOURCE_CLASSES.connectorDerived,
  manualAdmin: DECISION_AGENT_SOURCE_CLASSES.manualAdmin,
  // Standing Law 15 (2026-08-04): modeled evidence admits under its own class
  // and renders with the mandatory "Modeled" disclosure label instead of being
  // rejected (or mislabeled) at admission. The matrix stays closed otherwise.
  modeled: DECISION_AGENT_SOURCE_CLASSES.modeled,
} as const;

export const STRATEGIC_EXECUTION_CAPACITY_FORBIDDEN_SOURCE_CLASSES = {
  demo: 'demo',
  fixture: 'fixture',
  synthetic: 'synthetic',
  syntheticDemo: DECISION_AGENT_SOURCE_CLASSES.syntheticDemo,
  test: 'test',
} as const;

export const STRATEGIC_EXECUTION_CAPACITY_DERIVED_SOURCE_CLASSES = {
  decisionLedgerVerificationPlan: 'decision-ledger-verification-plan',
} as const;

const NO_STRATEGIC_EXECUTION_CAPACITY_BINDING_PLACEHOLDERS: readonly string[] = Object.freeze([]);

const strategicExecutionCapacityEvidenceEntry = defineModuleEvidenceEntry(
  STRATEGIC_EXECUTION_CAPACITY_EVIDENCE_KEYS.governedSnapshot,
  {
    sourceClass: DECISION_AGENT_SOURCE_CLASSES.manualAdmin,
    contextKind: STRATEGIC_EXECUTION_CAPACITY_CONTEXT_KINDS.governedSnapshot,
    schemaVersion: STRATEGIC_EXECUTION_CAPACITY_SCHEMA_VERSIONS.governedSnapshot,
    bindingPlaceholders: NO_STRATEGIC_EXECUTION_CAPACITY_BINDING_PLACEHOLDERS,
    domain: 'operations',
    freshnessCadence: 'weekly',
    requiredFor: ['monitoring', 'scenario', 'decision_pack', 'activation', 'ask_helix'],
    missingBehavior: 'block',
    description:
      'Approved strategic execution capacity evidence artifact bound to one tenant, active strategy, governed scope, source cut, and ingest approval.',
  }
);

export const STRATEGIC_EXECUTION_CAPACITY_MODULE_EVIDENCE_CONTRACT = defineModuleEvidenceContract(
  // K3 (2026-08-18): re-keyed to the module's canonical manifest id
  // (STRATEGIC_EXECUTION_CAPACITY_MODULE_ID in apps/api/src/decision-agent-runtime/
  // decision-agent-use-case-readiness-registry.ts). The registry is looked up by
  // manifest moduleId (governed-semantic-graph.ts:487); the previous hyphenated
  // key never matched and read as CONTRACT_ABSENT.
  'strategic_execution_capacity_diagnostic',
  {
    governed_snapshot: strategicExecutionCapacityEvidenceEntry,
  },
  {
    evidenceKeys: Object.values(STRATEGIC_EXECUTION_CAPACITY_EVIDENCE_KEYS),
    sourceClasses: [
      ...Object.values(STRATEGIC_EXECUTION_CAPACITY_ADMITTED_SOURCE_CLASSES),
      ...Object.values(STRATEGIC_EXECUTION_CAPACITY_DERIVED_SOURCE_CLASSES),
    ],
    contextKinds: Object.values(STRATEGIC_EXECUTION_CAPACITY_CONTEXT_KINDS),
    schemaVersions: Object.values(STRATEGIC_EXECUTION_CAPACITY_SCHEMA_VERSIONS),
    stateKeys: Object.values(STRATEGIC_EXECUTION_CAPACITY_STATE_KEYS),
    bindingPlaceholders: NO_STRATEGIC_EXECUTION_CAPACITY_BINDING_PLACEHOLDERS,
  }
);

export type StrategicExecutionCapacityModuleEvidenceContract =
  typeof STRATEGIC_EXECUTION_CAPACITY_MODULE_EVIDENCE_CONTRACT;
